import { useSyncExternalStore } from "react";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { getNowServerSnapshot, getNowSnapshot, subscribeNow } from "@/lib/nowStore";
import {
  UsersIcon,
  OrganizationsIcon,
  OnlineIcon,
  TrendingIcon,
  ChatIcon,
} from "@/components/icons";

export function StatsCard({
  icon,
  label,
  value,
  displayValue,
  helpText,
  description,
  suffix = "",
  highlight = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | undefined;
  displayValue?: string;
  helpText?: string;
  description?: string;
  suffix?: string;
  highlight?: boolean;
}) {
  return (
    <Card className={highlight ? "border-primary" : ""}>
      <CardContent className="flex flex-col items-center p-6 text-center">
        <div className={highlight ? "text-primary" : "text-muted"}>{icon}</div>
        {value === undefined ? (
          <Skeleton className="mt-2 h-8 w-16" />
        ) : (
          <p
            className={`mt-2 text-3xl font-bold ${highlight ? "text-primary" : "text-foreground"}`}
          >
            {displayValue ?? `${value.toLocaleString()}${suffix}`}
          </p>
        )}
        <div className="mt-1 flex items-center justify-center gap-1 text-sm text-muted">
          <span>{label}</span>
          {helpText && <InfoHint text={helpText} />}
        </div>
        {description && (
          <p className="mt-1 text-xs text-muted">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

export function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-background p-6 transition-all hover:bg-foreground/[0.02] hover:border-foreground/15 hover:shadow-sm">
      <div className="mb-4 inline-flex rounded-lg bg-primary/10 p-3 text-primary">
        {icon}
      </div>
      <h3 className="mb-2 text-xl font-semibold text-foreground">{title}</h3>
      <p className="text-sm text-muted">{description}</p>
    </div>
  );
}

export function TrendingList({
  items,
}: {
  items: Array<{
    id: string;
    name: string;
    memberCount: number;
    onlineCount: number;
    slug: string;
  }>;
}) {
  if (items.length === 0) {
    return (
      <p className="col-span-full text-center text-muted">
        No organizations yet. Be the first to create one!
      </p>
    );
  }

  return (
    <>
      {items.map((org) => (
        <Link key={org.id} href={`/org/${org.slug}`}>
          <Card className="transition-all hover:bg-foreground/[0.02] hover:border-foreground/15 hover:shadow-sm">
            <CardContent className="flex items-center justify-between p-4">
              <div>
                <h3 className="font-semibold text-foreground">{org.name}</h3>
                <p className="text-sm text-muted">{org.memberCount} members</p>
              </div>
              <Badge variant="success" className="bg-primary/20 text-primary">
                <OnlineIcon className="mr-1 h-3 w-3" />
                {org.onlineCount} online
              </Badge>
            </CardContent>
          </Card>
        </Link>
      ))}
    </>
  );
}

export function ActivityItem({
  activity,
}: {
  activity: {
    id: string;
    type: string;
    userName: string | null;
    orgName: string | null | undefined;
    createdAt: number;
  };
}) {
  const now = useSyncExternalStore(subscribeNow, getNowSnapshot, getNowServerSnapshot);

  const getActivityText = () => {
    switch (activity.type) {
      case "user_signup":
        return `${activity.userName || "Someone"} joined BlockyNetworks`;
      case "org_created":
        return `${activity.userName || "Someone"} created ${activity.orgName || "an organization"}`;
      case "member_joined":
        return `${activity.userName || "Someone"} joined ${activity.orgName || "an organization"}`;
      case "message_sent":
        return `New message in ${activity.orgName || "a chat"}`;
      default:
        return "Activity occurred";
    }
  };

  const getActivityIcon = () => {
    switch (activity.type) {
      case "user_signup":
        return <UsersIcon className="h-4 w-4" />;
      case "org_created":
        return <OrganizationsIcon className="h-4 w-4" />;
      case "member_joined":
        return <UsersIcon className="h-4 w-4" />;
      case "message_sent":
        return <ChatIcon className="h-4 w-4" />;
      default:
        return <OnlineIcon className="h-4 w-4" />;
    }
  };

  const timeAgo = (timestamp: number) => {
    const seconds = Math.floor((now - timestamp) / 1000);
    if (seconds < 60) return "just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-background p-3">
      <div className="text-muted">{getActivityIcon()}</div>
      <p className="flex-1 text-sm text-foreground">{getActivityText()}</p>
      <span className="text-xs text-muted">{timeAgo(activity.createdAt)}</span>
    </div>
  );
}

export const statsIcons = {
  users: <UsersIcon className="h-6 w-6" />,
  organizations: <OrganizationsIcon className="h-6 w-6" />,
  online: <OnlineIcon className="h-6 w-6 text-primary" />,
  trending: <TrendingIcon className="h-6 w-6" />,
};
